import moment from 'moment/moment'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { ComputeFactory } from '../../models/ComputeFactory'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { isPastDate } from '../../utils'
import { isForVendor } from '../../utils/records'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps } from '../PocketComponent'
import { usePSComponent } from '../PocketComponent'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { PackageView } from './PackageView'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (!urlParams.id) {
        return new RecordNotFoundError('Could not find package')
    }

    await RecordFactory.getInstance().fetchPackage({ id: urlParams.id } as any, 'view')
    const packageObj = await RecordFactory.getInstance().loadPackage(urlParams.id)
    if (!packageObj) {
        return new RecordNotFoundError('Could not find package')
    }
    await RecordFactory.getInstance().fetchPackage(packageObj, 'package')

    await RecordFactory.getInstance().fetchItemBookings(packageObj.item?.id ?? '')
    const client = packageObj.client
    const item = packageObj.item
    if (!client || !item) {
        return new RecordNotFoundError('Could not find package')
    }

    const [bookings, payments] = await Promise.all([
        ComputeFactory.getInstance().fetchPackageBookings(packageObj),
        ComputeFactory.getInstance().fetchPackagePayments(packageObj),
    ])

    return { packageObj, client, item, bookings, payments }
}

const render = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const navigate = useNavigate()

    usePageTitle('Package')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const { packageObj, client, item, bookings, payments } = data

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
    } | null>(null)

    const numRemainingSessions = useMemo(() => {
        if (bookings === undefined || packageObj.capacity === undefined) {
            return 0
        }

        return Math.max(packageObj.capacity - bookings.length, 0)
    }, [packageObj, bookings])

    const handleResend = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().sendPackage(packageObj, false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleSendReceipt = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().sendReceipt(packageObj)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleCancel = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().cancelPackage(packageObj)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleUndo = async () => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().rollbackTransaction(packageObj)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        buttons.push({ title: 'Edit', onClick: () => navigate(`/packages/${packageObj.id}/edit`) })

        if (!packageObj.paidDate && !packageObj.cancelledDate && !!packageObj.chargeAmount) {
            buttons.push({
                title: 'Charge',
                onClick: () => navigate(`/charge/package/${packageObj.id}`),
            })
        }
        else if (!packageObj.paidDate && !packageObj.cancelledDate) {
            buttons.push({
                title: 'Mark paid',
                onClick: () => navigate(`/charge/package/${packageObj.id}`),
            })
        }
        else if (
            packageObj.paidDate &&
            !packageObj.cancelledDate &&
            numRemainingSessions > 0 &&
            !isPastDate(moment(packageObj.expiredDate).startOf('day').add(1, 'days').toDate())
        ) {
            buttons.push({
                title: 'Schedule',
                onClick: () => navigate(`/bookings/new?packageId=${packageObj.id ?? ''}`),
            })
        }
        else {
            buttons.push({
                title: 'Copy',
                onClick: () => navigate(`/packages/${packageObj.id}/copy`),
            })
        }

        buttons.push({
            title: 'Resend',
            onClick: () =>
                setConfirmationModalData({
                    title: 'Resend package',
                    confirmationButtonText: 'Resend',
                    body: 'Would you like to send this package to this client?',
                    onConfirm: handleResend,
                }),
        })

        if (packageObj.markPaidDate) {
            buttons.push({
                title: 'Receipt',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Send receipt',
                        confirmationButtonText: 'Send receipt',
                        body: 'Would you like to send a receipt to this client?',
                        onConfirm: handleSendReceipt,
                    }),
            })
        }

        if (!packageObj.cancelledDate && !packageObj.completedDate) {
            buttons.push({
                title: 'Cancel',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Cancel package?',
                        confirmationButtonText: 'Cancel package',
                        body: 'This action cannot be undone.',
                        onConfirm: handleCancel,
                    }),
            })
        }

        if (packageObj.markPaidDate && isForVendor(packageObj, owner)) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Undo mark paid',
                        confirmationButtonText: 'Undo',
                        body: 'Would you like to undo this payment?',
                        onConfirm: handleUndo,
                    }),
            })
        }
        else if (
            packageObj.cancelledDate &&
            packageObj.source &&
            isForVendor(packageObj, owner)
        ) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Undo cancellation',
                        confirmationButtonText: 'Undo',
                        body: 'Would you like to undo the appointment cancellation?',
                        onConfirm: handleUndo,
                    }),
            })
        }

        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^((\/packages.*\/(new|edit|copy))|(\/charge\/package\/.*))/,
    })

    return (
        <BaseRecordView
            title="Package"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Packages',
                          onClick: () => navigate('/dashboards/packages'),
                      }
            }
            headerButtons={headerButtons}
            tag={packageObj.statusTag ?? undefined}
        >
            <PackageView
                packageObj={packageObj}
                isWorkflowReviewStep={false}
                client={client}
                item={item}
                bookings={bookings}
                payments={payments}
            />

            {confirmationModalData !== null && (
                <ConfirmationModal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    onConfirm={confirmationModalData.onConfirm}
                    title={confirmationModalData.title}
                    confirmButtonText={confirmationModalData.confirmationButtonText}
                    shouldHideCancelButton
                >
                    {confirmationModalData.body}
                </ConfirmationModal>
            )}
        </BaseRecordView>
    )
}

export const PackagePage = usePSComponent(render, viewWillAppear)
